import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import {
  preAuthorizationService,
  type PreAuthorization,
  type PreAuthStatus,
} from '../../../services/preAuthorizationService';
import EmptyState from '../../../components/ui/EmptyState';
import Spinner from '../../../components/ui/Spinner';
import { PRE_AUTH_STATUS_LABELS, PRE_AUTH_STATUS_BADGE } from '../../../domain/statuses';
import { parseApiDate } from '../../../lib/format';

const TYPE_LABELS: Record<string, string> = {
  procedure: 'Procedimiento',
  lab: 'Laboratorio',
  imaging: 'Imagen',
  medication: 'Medicamento',
  hospitalization: 'Hospitalización',
  consultation: 'Consulta',
};

const PreAuthorizationListPage: React.FC = () => {
  const navigate = useNavigate();
  const [preAuths, setPreAuths] = useState<PreAuthorization[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<PreAuthStatus | ''>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await preAuthorizationService.getAll({
        page,
        per_page: 15,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setPreAuths(res.data);
      setTotal(res.total);
    } catch {
      setPreAuths([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    return (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const totalPages = Math.ceil(total / 15);

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Pre-Autorizaciones' }]} />
          </div>
          <div className="col-sm-6 text-end">
            <Link to="/pre-authorizations/new" className="btn btn-primary">
              <i className="fa fa-plus me-1" /> Nueva Pre-Autorización
            </Link>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-5">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por número o servicio..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { setSearch(searchInput); setPage(1); }
                      }}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => { setSearch(searchInput); setPage(1); }}
                      aria-label="Buscar"
                    >
                      <i className="fa fa-search" />
                    </button>
                    {search && (
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
                        aria-label="Limpiar filtros"
                      >
                        <i className="fa fa-times" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value as PreAuthStatus | ''); setPage(1); }}
                  >
                    <option value="">Todos los estados</option>
                    {(Object.entries(PRE_AUTH_STATUS_LABELS) as [PreAuthStatus, string][]).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Número de Auth.</th>
                      <th>Paciente</th>
                      <th>Aseguradora</th>
                      <th>Tipo</th>
                      <th>Servicio</th>
                      <th>Estado</th>
                      <th>Fecha Solicitud</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={8} className="text-center py-4"><Spinner size="lg" /></td></tr>
                    ) : preAuths.length === 0 ? (
                      <EmptyState variant="compact" icon="fa-shield" title="No hay pre-autorizaciones registradas" action={{ label: 'Crear pre-autorización', to: '/pre-authorizations/new', icon: 'fa-plus' }} />
                    ) : preAuths.map((p) => (
                      <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pre-authorizations/${p.id}`)}>
                        <td><code>{p.authorization_number}</code></td>
                        <td>{p.patient ? `${p.patient.first_name} ${p.patient.last_name}` : '-'}</td>
                        <td>{p.patientInsurance?.company?.name ?? '-'}</td>
                        <td>{TYPE_LABELS[p.authorization_type] ?? p.authorization_type}</td>
                        <td>{p.service_name}</td>
                        <td><span className={PRE_AUTH_STATUS_BADGE[p.status]}>{PRE_AUTH_STATUS_LABELS[p.status]}</span></td>
                        <td>{formatDate(p.request_date)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Link to={`/pre-authorizations/${p.id}`} className="btn btn-sm btn-outline-primary" aria-label="Ver detalle">
                            <i className="fa fa-eye" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">{total} resultados</span>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p - 1)}>‹</button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                        <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(n)}>{n}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p + 1)}>›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreAuthorizationListPage;
